import React, { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Person from '@material-ui/icons/Person';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@material-ui/core';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';

interface GroupMember {
  displayName: string;
  mail: string;
}

interface MemberOption {
  displayName: string;
  mail: string;
  ownerRef: string;
}

// Same shape as ascentt.com's actual Backstage User entity names — every
// AD-synced user is registered as user:default/<local-part-with-dots>, the
// domain swapped for an underscore (e.g. sasi.sundaram@ascentt.com ->
// user:default/sasi.sundaram_ascentt.com). See existing catalog data.
const emailToOwnerRef = (mail: string) => `user:default/${mail.replace('@', '_')}`;

export const validateOwnerGroup = (value: string, validation: FieldValidation) => {
  if (!value || !value.startsWith('user:default/')) {
    validation.addError('Please select an Owner from the dropdown list');
  }
};

export const CustomOwnerGroupField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const config = useApi(configApiRef);

  const groupNameOption = uiSchema?.['ui:options']?.groupName;
  const groupNames: string[] = Array.isArray(groupNameOption)
    ? groupNameOption
    : groupNameOption
      ? [groupNameOption as string]
      : [];
  const backendUrl = config.getString('backend.baseUrl');

  const { title, description, placeholder, helperText } = schema;

  useEffect(() => {
    if (groupNames.length === 0) return;

    const fetchMembers = async () => {
      setLoading(true);
      try {
        const groupParam = groupNames.map(g => encodeURIComponent(g)).join(',');
        const response = await fetch(
          `${backendUrl}/api/planner/group-members?groupName=${groupParam}`,
        );
        if (!response.ok) {
          throw new Error('Failed to fetch group members');
        }
        const data = await response.json();
        setMembers(data.members || []);
      } catch (_) {
        // Failed to fetch group members
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, groupNames.join(',')]);

  const memberOptions: MemberOption[] = useMemo(
    () =>
      members.map(member => ({
        displayName: member.displayName,
        mail: member.mail,
        ownerRef: emailToOwnerRef(member.mail),
      })),
    [members],
  );

  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) return memberOptions;
    const searchTerm = inputValue.toLowerCase();
    return memberOptions.filter(
      option =>
        option.displayName.toLowerCase().includes(searchTerm) ||
        option.mail.toLowerCase().includes(searchTerm),
    );
  }, [memberOptions, inputValue]);

  const selectedOption = useMemo(
    () => memberOptions.find(opt => opt.ownerRef === formData) || null,
    [memberOptions, formData],
  );

  useEffect(() => {
    setInputValue(selectedOption ? selectedOption.displayName : '');
  }, [selectedOption]);

  const hasFormValidationErrors = rawErrors && rawErrors.length > 0;
  const errorMessage = hasFormValidationErrors
    ? 'Please select an Owner from the dropdown list'
    : undefined;
  const displayHelper = errorMessage || helperText || description;

  return (
    <Autocomplete
      options={filteredOptions}
      value={selectedOption}
      inputValue={inputValue}
      loading={loading}
      open={isOpen && (inputValue.length === 0 || filteredOptions.length > 0)}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      onInputChange={(_, newInputValue: string, reason: string) => {
        setInputValue(newInputValue);
        if (reason === 'clear') {
          onChange(undefined as any);
        }
      }}
      onChange={(_, newValue) => {
        if (newValue) {
          setInputValue(newValue.displayName);
          onChange(newValue.ownerRef);
          setIsOpen(false);
        } else {
          setInputValue('');
          onChange(undefined as any);
        }
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          fullWidth
          label={title || 'Owner'}
          error={hasFormValidationErrors}
          helperText={displayHelper}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Person />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.displayName
      }
      renderOption={(props: any, option) => (
        <Box component="li" {...props} display="flex" alignItems="center" style={{ gap: '8px' }}>
          <Person />
          <Box>
            <div>{option.displayName}</div>
            <div style={{ fontSize: '0.8em', color: '#888' }}>{option.mail}</div>
          </Box>
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.ownerRef === value.ownerRef}
      noOptionsText={loading ? 'Loading...' : 'No members found'}
      filterOptions={options => options}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      autoComplete
      autoHighlight
    />
  );
};
